/**
 * Maps items and adds separators between them.
 *
 * @param source
 * @param makeSeparator
 */
export function addSeparators<T>(
  source: T[],
  makeSeparator: (
    left: T,
    right: T,
    resultingIndex: number,
    newLength: number,
  ) => T,
): T[] {
  const result: T[] = []
  let previousItem: T | undefined
  let resultingIndex = 0
  for (let itemIndex = 0; itemIndex < source.length; itemIndex++) {
    const item = source[itemIndex]
    if (itemIndex === 0) {
      previousItem = item
      result.push(item)
      continue
    }

    const separator = makeSeparator(
      previousItem!,
      item,
      ++resultingIndex,
      source.length + Math.max(source.length - 2, 0),
    )
    resultingIndex++
    previousItem = item
    result.push(separator)
    result.push(item)
  }
  return result
}

/**
 * Whether left and right array items are `===`.
 *
 * @param left
 * @param right
 */
export function shallowItemsAreEqual<A extends ReadonlyArray<any>>(
  left: A,
  right: A,
) {
  if (left === right) {
    return true
  }

  if (left.length !== right.length) {
    return false
  }

  return left.every((lv, i) => right[i] === lv)
}

/**
 * Moves an element in an array from one position to another. The array is mutated.
 */
export function move<T>(source: T[], fromIndex: number, toIndex: number) {
  const element = source[fromIndex]
  source.splice(fromIndex, 1)
  source.splice(toIndex, 0, element)
}
